import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type CommunityPostsWidgetProps,
  type CommunityPostsThemingProps,
  POSTS_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionCommunityPosts for imperative control.
 */
export interface LegionCommunityPostsHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildCommunityPostsWidgetUrl(
  props: CommunityPostsWidgetProps
): string {
  const {
    baseUrl,
    pageSize = 10,
    filter = "recent",
    channelId,
    showFilters = true,
    showActions = true,
    userId,
    username,
    theme = "light",
  } = props;

  const url = new URL("/widget/community-posts", baseUrl);
  url.searchParams.set("pageSize", String(Math.min(Math.max(pageSize, 5), 50)));
  url.searchParams.set("filter", filter);
  url.searchParams.set("showFilters", String(showFilters));
  url.searchParams.set("showActions", String(showActions));
  url.searchParams.set("theme", theme);

  if (channelId) url.searchParams.set("channelId", channelId);
  if (userId) url.searchParams.set("userId", userId);
  if (username) url.searchParams.set("username", username);

  // Append theming params
  const themingKeys = Object.keys(POSTS_THEMING_PROP_TO_PARAM) as Array<
    keyof CommunityPostsThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(POSTS_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionCommunityPosts – a React component that embeds the Legion
 * Community Posts widget via an iframe.
 *
 * Supports postMessage authentication for features like liking and commenting.
 */
export const LegionCommunityPosts = forwardRef<
  LegionCommunityPostsHandle,
  CommunityPostsWidgetProps
>(function LegionCommunityPosts(props, ref) {
  const {
    pageSize = 10,
    filter,
    channelId,
    showFilters,
    showActions,
    userId,
    username,
    authToken,
    theme = "light",
    height = "800px",
    width = "100%",
    className,
    style,
    onLoad,
    onError,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildCommunityPostsWidgetUrl(props),
    [
      baseUrl, pageSize, filter, channelId, showFilters, showActions,
      userId, username, theme,
      ...Object.keys(POSTS_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  const handleLoad = useCallback(() => {
    // Send auth token via postMessage
    const iframe = iframeRef.current;
    if (authToken && iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: "AUTH_TOKEN", token: authToken },
        baseUrl
      );
    }
    onLoad?.({ widget: "community-posts", theme: theme ?? "light" });
  }, [onLoad, theme, authToken, baseUrl]);

  const handleError = useCallback(() => {
    onError?.("Failed to load community posts widget");
  }, [onError]);

  return (
    <div
      className={className}
      style={{ width, height, ...style }}
      data-testid="legion-community-posts"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Community Posts Widget"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        loading="lazy"
        onLoad={handleLoad}
        onError={handleError}
        style={{ border: "none" }}
      />
    </div>
  );
});

